import React, { useState, useEffect } from"react";
import { Filter, CheckCircle, Clock, XCircle, FileDown } from"lucide-react";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 Toast,
 ToastType,
 ModernButton,
 ModernSelect,
 ListPageSkeleton,
} from"../../components/ui";
import { DatePicker } from"../../components/form/DatePicker";
import { apiService } from"../../services/api";
import { formatCurrency, formatDateToBR } from"../../utils/formatters";
import { FiscalDocumentsListView } from"../../components/financial/FiscalDocumentsListView";
import { FiscalDocumentDetailsCard } from"../../components/financial/FiscalDocumentDetailsCard";

export interface FiscalDocument {
 id: string;
 saleId: string;
 type:"NFe";
 number?: string;
 series?: string;
 accessKey?: string;
 status:"draft"|"authorized"|"cancelled";
 issueDate?: string;
 emitterCnpj: string;
 emitterName: string;
 emitterIe?: string;
 customerType:"PF"|"PJ";
 customerDocument: string;
 customerName: string;
 customerIe?: string;
 vehicleId: string;
 description: string;
 chassi?: string;
 renavam?: string;
 modelYear: number;
 cfop: string;
 productValue: number | string;
 chargesValue: number | string;
 discountValue: number | string;
 totalValue: number | string;
 icmsBase: number | string;
 icmsValue: number | string;
 ipiValue: number | string;
 pisValue: number | string;
 cofinsValue: number | string;
 paymentType:"cash"|"financing";
 invoiceReference?: string;
 xml?: string;
 xmlPath?: string;
 protocol?: string;
 sefazResponse?: any;
 createdAt: string;
 updatedAt: string;
 sale?: any;
 vehicle?: any;
}

export const FiscalDocumentsPage: React.FC = () => {
 const [documents, setDocuments] = useState<FiscalDocument[]>([]);
 const [isLoading, setIsLoading] = useState(true);
 const [toast, setToast] = useState<{
 message: string;
 type: ToastType;
 } | null>(null);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const [selectedDocument, setSelectedDocument] =
 useState<FiscalDocument | null>(null);
 const [showDetails, setShowDetails] = useState(false);
 // Helper function to get first day of current month
 const getFirstDayOfMonth = () => {
 const now = new Date();
 return new Date(now.getFullYear(), now.getMonth(), 1)
 .toISOString()
 .split("T")[0];
 };

 // Helper function to get today's date
 const getToday = () => {
 return new Date().toISOString().split("T")[0];
 };

 const [filters, setFilters] = useState({
 saleId:"",
 status:"all"as"all"|"draft"|"authorized"|"cancelled",
 startDate: getFirstDayOfMonth(),
 endDate: getToday(),
 });
 const [exportDialogOpen, setExportDialogOpen] = useState(false);

 useEffect(() => {
 fetchDocuments();
 }, [page, filters]);

 const fetchDocuments = async () => {
 try {
 setIsLoading(true);
 const response = await apiService.getFiscalDocuments({
 saleId: filters.saleId || undefined,
 status: filters.status !=="all"? filters.status : undefined,
 page,
 limit: 50,
 });
 let filteredDocs = response.fiscalDocuments || response.documents || [];

 // Apply date filters
 if (filters.startDate) {
 filteredDocs = filteredDocs.filter(
 (doc: FiscalDocument) =>
 doc.issueDate &&
 new Date(doc.issueDate) >= new Date(filters.startDate)
 );
 }
 if (filters.endDate) {
 filteredDocs = filteredDocs.filter(
 (doc: FiscalDocument) =>
 doc.issueDate &&
 new Date(doc.issueDate) <= new Date(filters.endDate)
 );
 }

 setDocuments(filteredDocs);
 setTotalPages(response.pagination?.totalPages || 1);
 } catch (err) {
 console.error("Erro ao carregar documentos fiscais:", err);
 setToast({
 message:"Erro ao carregar documentos fiscais",
 type:"error",
 });
 } finally {
 setIsLoading(false);
 }
 };

 const handleViewDetails = async (documentId: string) => {
 try {
 const doc = await apiService.getFiscalDocumentById(documentId);
 setSelectedDocument(doc);
 setShowDetails(true);
 } catch (err) {
 console.error("Erro ao carregar detalhes do documento:", err);
 setToast({
 message:"Erro ao carregar detalhes do documento",
 type:"error",
 });
 }
 };

 const handleCloseDetails = () => {
 setShowDetails(false);
 setSelectedDocument(null);
 };

 const handleExportCSV = () => {
 const headers = [
"Número",
"Série",
"Chave de Acesso",
"Status",
"Data de Emissão",
"Emitente",
"Cliente",
"Valor Total",
"Protocolo",
 ];

 const rows = documents.map((doc) => [
 doc.number ||"",
 doc.series ||"",
 doc.accessKey ||"",
 doc.status,
 doc.issueDate ? formatDateToBR(doc.issueDate) :"",
 doc.emitterName,
 doc.customerName,
 formatCurrency(parseFloat(doc.totalValue.toString())),
 doc.protocol ||"",
 ]);

 const csvContent = [
 headers.join(","),
 ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
 ].join("\n");

 const blob = new Blob([csvContent], { type:"text/csv;charset=utf-8;"});
 const link = document.createElement("a");
 const url = URL.createObjectURL(blob);
 link.setAttribute("href", url);
 link.setAttribute(
"download",
 `documentos-fiscais-${new Date().toISOString().split("T")[0]}.csv`
 );
 link.style.visibility ="hidden";
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);

 setToast({
 message:"Documentos exportados com sucesso!",
 type:"success",
 });
 setExportDialogOpen(false);
 };

 const handleDownloadXML = async (documentId: string) => {
 try {
 const blob = await apiService.downloadFiscalDocumentXml(documentId);
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `nfe-${documentId}.xml`;
 document.body.appendChild(a);
 a.click();
 window.URL.revokeObjectURL(url);
 document.body.removeChild(a);

 setToast({
 message:"XML baixado com sucesso!",
 type:"success",
 });
 } catch (err) {
 console.error("Erro ao baixar XML:", err);
 setToast({
 message:"Erro ao baixar XML",
 type:"error",
 });
 }
 };

 const getStatusIcon = (status: FiscalDocument["status"]) => {
 switch (status) {
 case"authorized":
 return <CheckCircle className="h-5 w-5 text-green-600"/>;
 case"draft":
 return <Clock className="h-5 w-5 text-yellow-600"/>;
 case"cancelled":
 return <XCircle className="h-5 w-5 text-red-600"/>;
 default:
 return <Clock className="h-5 w-5 text-muted-foreground"/>;
 }
 };

 const getStatusLabel = (status: FiscalDocument["status"]) => {
 const labels: Record<FiscalDocument["status"], string> = {
 draft:"Rascunho",
 authorized:"Autorizada",
 cancelled:"Cancelada",
 };
 return labels[status] || status;
 };

 return (
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <h1 className="text-2xl font-bold text-foreground font-display">
 Documentos Fiscais
 </h1>
 <ModernButton
 variant="outline"
 onClick={() => setExportDialogOpen(true)}
 disabled={documents.length === 0}
 >
 <FileDown className="h-4 w-4 mr-2"/>
 Exportar
 </ModernButton>
 </div>

 {/* Filters */}
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <Filter className="h-5 w-5 mr-2"/>
 Filtros
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <ModernSelect
 label="Status"
 value={filters.status}
 onChange={(e) =>
 setFilters({ ...filters, status: e.target.value as "all" | "draft" | "authorized" | "cancelled" })
 }
 options={[
 { value: "all", label: "Todos" },
 { value: "draft", label: "Rascunho" },
 { value: "authorized", label: "Autorizada" },
 { value: "cancelled", label: "Cancelada" },
 ]}
 placeholder=""
 />
 <div>
 <DatePicker
 label="Data Inicial"
 value={filters.startDate}
 onChange={(value) =>
 setFilters({ ...filters, startDate: value })
 }
 placeholder="Selecione a data inicial"
 />
 </div>
 <div>
 <DatePicker
 label="Data Final"
 value={filters.endDate}
 onChange={(value) => setFilters({ ...filters, endDate: value })}
 placeholder="Selecione a data final"
 />
 </div>
 <div className="flex items-end">
 <ModernButton
 variant="outline"
 onClick={() =>
 setFilters({
 saleId:"",
 status:"all",
 startDate:"",
 endDate:"",
 })
 }
 className="w-full"
 >
 Limpar Filtros
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Documents List */}
 {isLoading && documents.length === 0 ? (
 <ListPageSkeleton rows={6} showFilters={false} />
 ) : (
 <FiscalDocumentsListView
 documents={documents}
 isLoading={isLoading}
 onViewDetails={handleViewDetails}
 onDownloadXML={handleDownloadXML}
 getStatusIcon={getStatusIcon}
 getStatusLabel={getStatusLabel}
 page={page}
 totalPages={totalPages}
 onPageChange={setPage}
 />
 )}

 {/* Document Details Modal */}
 {showDetails && selectedDocument && (
 <FiscalDocumentDetailsCard
 document={selectedDocument}
 onClose={handleCloseDetails}
 onDownloadXML={handleDownloadXML}
 />
 )}

 {/* Export Options - Inline */}
 {exportDialogOpen && (
 <div className="p-4 border border-border rounded-lg bg-card space-y-4">
 <div className="flex items-center justify-between">
 <h3 className="font-semibold text-foreground">Exportar Documentos Fiscais</h3>
 <ModernButton variant="outline" size="sm" onClick={() => setExportDialogOpen(false)} className="px-2">x</ModernButton>
 </div>
 <p className="text-sm text-muted-foreground">Selecione o formato de exportacao:</p>
 <div className="flex gap-2">
 <ModernButton onClick={handleExportCSV} className="flex-1">
 <FileDown className="h-4 w-4 mr-2"/>
 Exportar CSV
 </ModernButton>
 <ModernButton variant="outline" onClick={() => setExportDialogOpen(false)}>Cancelar</ModernButton>
 </div>
 </div>
 )}

 {toast && (
 <Toast
 message={toast.message}
 type={toast.type}
 onClose={() => setToast(null)}
 />
 )}
 </div>
 );
};
